import { useRef, useState } from "react";
import { Mic, MicOff, Send, Paperclip, Globe, Square, Loader2, Check, Image as ImageIcon, X } from "lucide-react";

const LANGS = [
  { code: "fr-FR", label: "Français", short: "FR" },
  { code: "en-US", label: "English", short: "EN" },
  { code: "es-ES", label: "Español", short: "ES" },
  { code: "de-DE", label: "Deutsch", short: "DE" },
  { code: "it-IT", label: "Italiano", short: "IT" },
  { code: "pt-PT", label: "Português", short: "PT" },
  { code: "ar-SA", label: "العربية", short: "AR" },
  { code: "zh-CN", label: "中文", short: "ZH" },
  { code: "ja-JP", label: "日本語", short: "JA" },
  { code: "hi-IN", label: "हिन्दी", short: "HI" },
];
const RATIOS = [{ v: "1:1" }, { v: "16:9" }, { v: "9:16" }];
const SIZES = ["1K", "2K", "4K"];

export function VoiceControlBar({
  state, onSend, onSendFile, handsFree, micReady = false, onToggleHandsFree, recogSupported, busy,
  recognitionLang = "fr-FR", onSetLang, whisperRecording, onWhisperToggle, whisperDefault = false,
  imgSize = "1K", imgRatio = "1:1", onSetImgSize, onSetImgRatio,
  pendingImage, onAnalyzeImage, onEditImage, onClearPending,
}) {
  const [text, setText] = useState("");
  const [langOpen, setLangOpen] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const fileRef = useRef(null);

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (busy) return;
    if (pendingImage) {
      if (!t) return;
      onEditImage?.(t);
      setText("");
      return;
    }
    if (!t) return;
    onSend(t);
    setText("");
  };

  const active = handsFree && recogSupported && micReady;
  const current = LANGS.find((l) => l.code === recognitionLang) || LANGS[0];

  const handleWhisper = async () => {
    if (!onWhisperToggle) return;
    if (whisperRecording) {
      setTranscribing(true);
      try { await onWhisperToggle(); } finally { setTranscribing(false); }
    } else {
      await onWhisperToggle();
    }
  };

  const voiceLive = whisperRecording || active || state === "listening" || state === "speaking";
  const micIsStop = whisperRecording;
  const onMicClick = whisperDefault ? handleWhisper : onToggleHandsFree;
  const micDisabled = whisperDefault ? transcribing : !recogSupported;
  const micTitle = whisperDefault
    ? (whisperRecording ? "Arrêter et envoyer" : "Appuyer pour parler (Whisper)")
    : !recogSupported
    ? "Entrée vocale non prise en charge"
    : active
    ? "Couper le micro (mains libres actif)"
    : "Activer l'écoute mains libres";

  return (
    <div className="space-y-4">
      {pendingImage && (
        <div className="flex items-center gap-3 rounded-2xl border border-cyan-500/25 bg-[rgba(8,16,30,0.7)] p-2" data-testid="pending-image-chip">
          <img src={pendingImage.dataUrl} alt={pendingImage.name} className="h-12 w-12 object-cover rounded-xl shrink-0 border border-cyan-500/20" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white truncate">{pendingImage.name}</div>
            <div className="text-[10px] text-slate-400 leading-tight">
              Écris une instruction puis <span className="text-cyan-300">Entrée</span> pour retoucher, ou
            </div>
          </div>
          <button
            type="button"
            data-testid="pending-analyze-button"
            onClick={onAnalyzeImage}
            className="rounded-full px-3 py-1.5 text-[10px] font-jb uppercase tracking-[0.12em] border border-cyan-500/25 text-slate-200 hover:text-white shrink-0"
          >
            Analyser
          </button>
          <button
            type="button"
            data-testid="pending-clear-button"
            onClick={onClearPending}
            className="p-1.5 rounded-full border border-cyan-500/25 text-slate-400 hover:text-white shrink-0"
            aria-label="Retirer l'image"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* hidden file picker */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf,.txt,.md,.csv,.docx,.xlsx"
        hidden
        data-testid="assistant-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSendFile?.(f);
          e.target.value = "";
        }}
      />

      {/* message pill — paperclip · input · gradient send */}
      <form
        onSubmit={submit}
        data-testid="composer-pill"
        className="flex items-center gap-2 rounded-full border border-cyan-500/25 bg-[rgba(8,16,30,0.72)] backdrop-blur-md p-2 shadow-[0_0_26px_rgba(0,240,255,0.1)] focus-within:border-cyan-400/60 transition-colors"
      >
        <button
          type="button"
          data-testid="assistant-file-button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          title="Joindre un fichier (image, PDF, texte…)"
          aria-label="Joindre un fichier"
          className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-cyan-300 bg-white/[0.04] border border-cyan-500/20 hover:bg-cyan-500/10 transition-colors disabled:opacity-40"
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <input
          data-testid="assistant-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={pendingImage ? "Décris la retouche (ex : rends le ciel violet)…" : "Écrivez un message…"}
          disabled={busy}
          className="flex-1 min-w-0 bg-transparent px-1 text-sm text-white outline-none placeholder:text-slate-500 disabled:opacity-60"
        />
        <button
          data-testid="assistant-send-button"
          type="submit"
          disabled={busy || !text.trim()}
          aria-label="Envoyer"
          className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-cyan-400 to-blue-600 shadow-[0_0_18px_rgba(0,240,255,0.5)] hover:brightness-110 transition disabled:opacity-40 disabled:shadow-none"
        >
          <Send className="h-[18px] w-[18px]" />
        </button>
      </form>

      {/* big central mic flanked by audio waveforms */}
      <div className="flex items-center justify-center gap-5">
        <Waveform side="left" live={voiceLive} />
        <button
          data-testid="mic-trigger-button"
          type="button"
          onClick={onMicClick}
          disabled={micDisabled}
          title={micTitle}
          className="relative h-16 w-16 shrink-0 rounded-full flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50"
          style={{
            background: micIsStop
              ? "linear-gradient(135deg,#ff5f6b,#b4245a)"
              : "linear-gradient(135deg,#00e5ff 0%,#4f7bff 55%,#8b5cf6 100%)",
            boxShadow: voiceLive ? "0 0 36px rgba(0,240,255,0.65)" : "0 0 22px rgba(0,240,255,0.4)",
          }}
        >
          {transcribing ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : micIsStop ? (
            <Square className="h-5 w-5 fill-white" />
          ) : active || whisperDefault ? (
            <Mic className="h-6 w-6" />
          ) : (
            <MicOff className="h-6 w-6" />
          )}
          {voiceLive && <span className="absolute inset-0 rounded-full border-2 border-cyan-300/70 animate-ping" />}
        </button>
        <Waveform side="right" live={voiceLive} />
      </div>

      {/* secondary controls (desktop/tablet only — keep mobile clean like the mockup) */}
      <div className="hidden sm:flex items-center justify-center gap-2.5 flex-wrap">
        {!whisperDefault && (
          <button
            type="button"
            data-testid="whisper-mic-button"
            onClick={handleWhisper}
            disabled={transcribing}
            title="Dictée multilingue — détection automatique (Whisper)"
            aria-label="Dictée multilingue"
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-jb uppercase tracking-widest transition-colors ${
              whisperRecording ? "border-rose-400/60 text-rose-300 bg-rose-500/10" : "border-cyan-500/25 text-slate-400 hover:text-cyan-300"
            }`}
          >
            {transcribing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : whisperRecording ? <Square className="h-3 w-3 fill-rose-300" /> : <Globe className="h-3.5 w-3.5" />}
            Dictée
          </button>
        )}

        <div className="relative">
          <button
            type="button"
            data-testid="lang-selector-button"
            onClick={() => setLangOpen((o) => !o)}
            title="Langue de la reconnaissance vocale"
            className="flex items-center gap-1.5 rounded-full border border-cyan-500/25 px-2.5 py-1 text-[10px] font-jb uppercase tracking-widest text-slate-400 hover:text-cyan-300 transition-colors"
          >
            <Globe className="h-3.5 w-3.5" /> {current.short}
          </button>
          {langOpen && (
            <div className="absolute bottom-full left-0 z-20 mb-1.5 max-h-60 w-40 overflow-y-auto no-scrollbar rounded-xl border border-cyan-500/25 bg-[rgba(3,8,18,0.97)] p-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => { onSetLang?.(l.code); setLangOpen(false); }}
                  className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[12px] transition-colors ${
                    l.code === recognitionLang ? "bg-cyan-500/15 text-white" : "text-slate-300 hover:bg-white/5"
                  }`}
                >
                  {l.label}
                  {l.code === recognitionLang && <Check className="h-3.5 w-3.5 text-cyan-300" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            data-testid="img-settings-button"
            onClick={() => setImgOpen((o) => !o)}
            title="Format et résolution des images générées"
            className="flex items-center gap-1.5 rounded-full border border-cyan-500/25 px-2.5 py-1 text-[10px] font-jb uppercase tracking-widest text-slate-400 hover:text-cyan-300 transition-colors"
          >
            <ImageIcon className="h-3.5 w-3.5" /> {imgRatio} · {imgSize}
          </button>
          {imgOpen && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 z-20 mb-1.5 w-52 rounded-xl border border-cyan-500/25 bg-[rgba(3,8,18,0.97)] p-2.5 space-y-2.5">
              <div>
                <div className="font-jb text-[9px] tracking-[0.2em] text-slate-500 uppercase mb-1.5">Format</div>
                <div className="flex gap-1">
                  {RATIOS.map((r) => (
                    <button
                      key={r.v}
                      type="button"
                      data-testid={`img-ratio-${r.v}`}
                      onClick={() => onSetImgRatio?.(r.v)}
                      className={`flex-1 rounded-lg px-1.5 py-1.5 text-[10px] font-jb border transition-colors ${
                        imgRatio === r.v ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-cyan-500/20 text-slate-300 hover:text-white"
                      }`}
                    >
                      {r.v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-jb text-[9px] tracking-[0.2em] text-slate-500 uppercase mb-1.5">Résolution</div>
                <div className="flex gap-1">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`img-size-${s}`}
                      onClick={() => onSetImgSize?.(s)}
                      className={`flex-1 rounded-lg px-1.5 py-1.5 text-[10px] font-jb border transition-colors ${
                        imgSize === s ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-cyan-500/20 text-slate-300 hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="hidden sm:block text-center font-jb text-[10px] text-slate-500 tracking-wide px-2">
        {whisperRecording
          ? (whisperDefault ? "ENREGISTREMENT… APPUYEZ SUR LE MICRO POUR ENVOYER" : "ENREGISTREMENT… APPUYEZ SUR DICTÉE POUR TRANSCRIRE")
          : whisperDefault
          ? "WHISPER PAR DÉFAUT — APPUYEZ SUR LE MICRO POUR PARLER"
          : !recogSupported
          ? "TAPEZ VOS COMMANDES · DICTÉE = MULTILINGUE"
          : active
          ? "PARLEZ QUAND VOUS VOULEZ — SARA VOUS ÉCOUTE"
          : handsFree
          ? "APPUYEZ SUR LE MICRO POUR DÉMARRER L'ÉCOUTE"
          : "MICRO COUPÉ — DICTÉE POUR PARLER DANS TOUTE LANGUE"}
      </p>
    </div>
  );
}

function Waveform({ side, live }) {
  const color = side === "left" ? "#22d3ee" : "#a855f7";
  const heights = [12, 22, 30, 20, 14];
  return (
    <div className={`flex items-center gap-1 h-9 ${live ? "" : "wave-idle"}`} aria-hidden="true">
      {heights.map((h, i) => (
        <span
          key={i}
          className="wave-bar"
          style={{ height: `${h}px`, background: color, boxShadow: `0 0 6px ${color}`, animationDelay: `${i * 0.12}s` }}
        />
      ))}
    </div>
  );
}
